import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useViewMode } from '../context/ViewModeContext'
import { duration, ease } from '../lib/motion'

const PATHS = [
  {
    id: 'business',
    label: 'I need a website that converts',
    detail: 'See the services, process and pricing.',
    cta: 'Explore services',
    target: '#philosophy',
  },
  {
    id: 'portfolio',
    label: "I'm reviewing your work",
    detail: 'Case studies, process, and how the decisions were made.',
    cta: 'View case studies',
    target: '#transformations',
  },
]

function scrollToTarget(target) {
  // Give the DOM a frame to finish reordering before measuring position.
  requestAnimationFrame(() => {
    const el = document.querySelector(target)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

export default function JourneyFork() {
  const { mode, setMode, resetMode, fromLink } = useViewMode()

  // If mode arrived via ?ref= link, honor it with the same scroll a click
  // would have triggered — but only once, on mount, not on every mode change.
  const didScrollForLink = useRef(false)
  useEffect(() => {
    if (fromLink && mode && !didScrollForLink.current) {
      didScrollForLink.current = true
      const path = PATHS.find((p) => p.id === mode)
      if (path) scrollToTarget(path.target)
    }
  }, [fromLink, mode])

  const choose = (path) => {
    setMode(path.id)
    scrollToTarget(path.target)
  }

  return (
    <section
      id="journey-fork"
      // py-10/14, not the sitewide py-24: this is a quick decision point
      // between the hero and the rest of the page, deliberately compact so
      // it reads as a fork in the road, not a full section of its own.
      className="py-10 md:py-14 border-t"
      style={{ borderColor: 'var(--border)' }}
    >
      <div className="container-bw">
        <AnimatePresence mode="wait">
          {!mode ? (
            <motion.div
              key="fork"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: duration.base, ease: ease.out }}
            >
              <p className="eyebrow mb-6 text-center">Why are you here?</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {PATHS.map((path) => (
                  <button
                    key={path.id}
                    onClick={() => choose(path)}
                    className="group text-left rounded-2xl border p-8 md:p-10 transition-colors"
                    style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--border-strong)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                  >
                    <div className="flex items-start justify-between gap-6">
                      <h2
                        className="font-display text-xl md:text-2xl font-medium"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {path.label}
                      </h2>
                      <span
                        className="shrink-0 h-9 w-9 rounded-full border flex items-center justify-center text-sm transition-transform group-hover:translate-x-1"
                        style={{ borderColor: 'var(--border-strong)', color: 'var(--text-primary)' }}
                      >
                        →
                      </span>
                    </div>
                    <p className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {path.detail}
                    </p>
                    <p
                      className="mt-6 text-sm font-medium underline underline-offset-4 inline-block"
                      style={{ color: 'var(--accent)' }}
                    >
                      {path.cta}
                    </p>
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="chosen"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: duration.fast, ease: ease.out }}
              className="flex flex-wrap items-center justify-center gap-3 text-sm"
              style={{ color: 'var(--text-muted)' }}
            >
              <span>
                {fromLink
                  ? mode === 'portfolio'
                    ? "You're viewing this via a recruiter link, showing case studies first."
                    : "You're viewing this via a shared link, showing services first."
                  : mode === 'portfolio'
                    ? 'Showing this site as a case study review.'
                    : 'Showing this site for a project inquiry.'}
              </span>
              <button
                onClick={resetMode}
                className="underline underline-offset-4"
                style={{ color: 'var(--text-secondary)' }}
              >
                Not this, switch view
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}
